import {Text} from '@rdcomponents/text/text';
import {ReactElement} from 'react';

import {useLocale} from '../../../../../../../provider/locale/locale-hook';
import {LangKeyType} from '../../../../../../../provider/locale/translation/type';
import {ProvidersIdsEnum} from '../../../../../../../util/type';

type ConstraintsImageType = {
    minResolution: string;
    maxResolution: string;
    minSize: LangKeyType;
    maxSize: LangKeyType;
    formats: Array<string>;
    extra?: LangKeyType;
};

const CATALOG_IMAGE_CONSTRAINTS: Partial<Record<ProvidersIdsEnum, ConstraintsImageType>> = {
    [ProvidersIdsEnum.yandex]: {
        minResolution: '720×720',
        maxResolution: '3000×3000',
        minSize: 'COMPANY_FORM__GALLERY__HINT__MIN_SIZE',
        maxSize: 'COMPANY_FORM__GALLERY__HINT__MAX_SIZE',
        formats: ['.jpg', '.jpeg', '.png'],
    },
    [ProvidersIdsEnum.google]: {
        minResolution: '720×720',
        maxResolution: '3000×3000',
        minSize: 'COMPANY_FORM__GALLERY__HINT__MIN_SIZE',
        maxSize: 'COMPANY_FORM__GALLERY__HINT__MAX_SIZE__GOOGLE',
        formats: ['.jpg', '.jpeg', '.png'],
    },
    [ProvidersIdsEnum.doubleGis]: {
        minResolution: '600×600',
        maxResolution: '7000×7000',
        minSize: 'COMPANY_FORM__GALLERY__HINT__MIN_SIZE',
        maxSize: 'COMPANY_FORM__GALLERY__HINT__MAX_SIZE',
        formats: ['.jpg', '.jpeg'],
        extra: 'COMPANY_FORM__GALLERY__HINT__2GIS__PROPORTIONS',
    },
};

type PropsType = {
    catalogId: ProvidersIdsEnum;
    type: 'photo' | 'video';
};

export function UploadCompanyFormDescription(props: PropsType): ReactElement {
    const {catalogId, type} = props;
    const {getLocalizedString} = useLocale();
    const cfg = CATALOG_IMAGE_CONSTRAINTS[catalogId];

    const photoReplacements: Record<string, string> = {
        minResolution: cfg?.minResolution || '720×720',
        maxResolution: cfg?.maxResolution || '3000×3000',
        minSize: getLocalizedString(cfg?.minSize || 'COMPANY_FORM__GALLERY__HINT__MIN_SIZE'),
        maxSize: getLocalizedString(cfg?.maxSize || 'COMPANY_FORM__GALLERY__HINT__MAX_SIZE'),
        formats: cfg?.formats.join(' ') || '.jpg .jpeg .png',
        extra: cfg?.extra ? `\n${getLocalizedString(cfg.extra)}` : '',
    };

    if (type === 'photo') {
        return (
            <Text
                secondary
                small
                stringKey="COMPANY_FORM__UPLOAD_PHOTO_BUTTON__DESCRIPTION"
                valueMap={photoReplacements}
            />
        );
    }

    return <Text secondary small stringKey="COMPANY_FORM__UPLOAD_VIDEO_BUTTON__DESCRIPTION" />;
}

/**
 * Upload errors come as JSON keyed by the file field, but a proxy in between may answer with an HTML page
 * (413, 502) or with JSON of another shape. Each such case must still mark the tile as failed, otherwise
 * the file looks uploaded while it never reached the server.
 */
export function getUploadErrorMessages(error: unknown, key: string, fallback: Array<string>): Array<string> {
    if (!(error instanceof Error)) {
        return fallback;
    }

    try {
        const parsed: unknown = JSON.parse(error.message);
        const messages: unknown = parsed && typeof parsed === 'object' ? Reflect.get(parsed, key) : null;

        if (Array.isArray(messages) && messages.length > 0 && messages.every((item) => typeof item === 'string')) {
            return messages as Array<string>;
        }
    } catch {
        // not JSON at all, e.g. an nginx error page
    }

    return fallback;
}
